import React from 'react'
import { X } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import {
  formatBetweenBounds,
  isValidBetweenBounds,
  parseBetweenBounds,
} from '@/shared/lib/data-field-bounds'
import {
  canAddNumberAcceptableRule,
  DATA_FIELD_TEXT_MAX_LENGTH,
  findContradictoryAcceptableConditions,
  isBetweenValidationType,
  patchForValidationTypeChange,
  shouldShowDefinedValues,
} from '@/shared/lib/data-field-config'
import type { DataFieldValidationEditorProps } from './types'

const AcceptableValueRowInline = ({
  rowIndex,
  row,
  fieldTypeCode,
  validationTypeOptions,
  fieldValidationOptions,
  labels,
  idPrefix,
  showAcceptableRowActive,
  showValidationErrors,
  isContradictory,
  onPatchAcceptable,
  onRemoveAcceptable,
}: {
  rowIndex: number
  row: DataFieldValidationEditorProps['acceptableValues'][number]
  fieldTypeCode: string | null
  validationTypeOptions: DataFieldValidationEditorProps['validationTypeOptions']
  fieldValidationOptions: DataFieldValidationEditorProps['validationTypeOptions']
  labels: DataFieldValidationEditorProps['labels']
  idPrefix: string
  showAcceptableRowActive: boolean
  showValidationErrors: boolean
  isContradictory: boolean
  onPatchAcceptable: DataFieldValidationEditorProps['onPatchAcceptable']
  onRemoveAcceptable: DataFieldValidationEditorProps['onRemoveAcceptable']
}) => {
  const betweenBounds = parseBetweenBounds(row.value)
  const isBetween = isBetweenValidationType(
    row.instrumentTaskDataFieldValidationTypeId,
    validationTypeOptions,
  )
  const betweenInvalid = isBetween && !isValidBetweenBounds(betweenBounds.min, betweenBounds.max)
  const markInvalid = isContradictory && showValidationErrors

  return (
    <div className="space-y-1">
      <div className="flex items-start gap-1">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {labels.acceptableIfLabel && (
            <span className="shrink-0 text-sm text-muted-foreground">
              {labels.acceptableIfLabel}
            </span>
          )}
          <Select
            value={row.instrumentTaskDataFieldValidationTypeId}
            onValueChange={(newValidationTypeId) =>
              onPatchAcceptable(rowIndex, {
                instrumentTaskDataFieldValidationTypeId: newValidationTypeId,
                ...patchForValidationTypeChange(
                  row.value,
                  row.instrumentTaskDataFieldValidationTypeId,
                  newValidationTypeId,
                  validationTypeOptions,
                ),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-44">
              <SelectValue placeholder={labels.acceptableValidationTypePlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {fieldValidationOptions.map((validationOption) => (
                <SelectItem key={validationOption.id} value={String(validationOption.id)}>
                  {validationOption.validationTypeDesc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isBetween ? (
            <div className="flex w-full gap-2 sm:w-auto">
              <Input
                className="flex-1 sm:w-28 sm:flex-none"
                value={betweenBounds.min}
                onChange={(event) =>
                  onPatchAcceptable(rowIndex, {
                    value: formatBetweenBounds(event.target.value, betweenBounds.max),
                  })
                }
                placeholder={labels.acceptableBetweenMin}
                inputMode="decimal"
                aria-invalid={betweenInvalid || markInvalid || undefined}
              />
              <Input
                className="flex-1 sm:w-28 sm:flex-none"
                value={betweenBounds.max}
                onChange={(event) =>
                  onPatchAcceptable(rowIndex, {
                    value: formatBetweenBounds(betweenBounds.min, event.target.value),
                  })
                }
                placeholder={labels.acceptableBetweenMax}
                inputMode="decimal"
                aria-invalid={betweenInvalid || markInvalid || undefined}
              />
            </div>
          ) : (
            <Input
              className="w-full sm:w-32"
              value={row.value}
              onChange={(event) => {
                const newValue = event.target.value
                if (
                  (fieldTypeCode === 'number' || fieldTypeCode === 'linear') &&
                  newValue !== '' &&
                  !/^-?\d*\.?\d*$/.test(newValue)
                ) {
                  return
                }
                onPatchAcceptable(rowIndex, { value: newValue })
              }}
              placeholder={labels.acceptableValuePlaceholder}
              inputMode={
                fieldTypeCode === 'number' || fieldTypeCode === 'linear' ? 'decimal' : undefined
              }
              aria-invalid={
                (showValidationErrors && row.value.trim() === '') || markInvalid || undefined
              }
            />
          )}
          {fieldTypeCode !== 'number' && fieldTypeCode !== 'linear' && (
            <label className="flex shrink-0 items-center gap-1 text-xs">
              <Checkbox
                checked={row.isAcceptable}
                onCheckedChange={(checked) =>
                  onPatchAcceptable(rowIndex, { isAcceptable: checked === true })
                }
              />
              {labels.acceptableIsAcceptable}
            </label>
          )}
          {showAcceptableRowActive && labels.acceptableRowActive && (
            <label className="flex shrink-0 items-center gap-1 text-xs">
              <Checkbox
                id={`${idPrefix}-acc-act-${rowIndex}`}
                checked={row.active}
                onCheckedChange={(checked) =>
                  onPatchAcceptable(rowIndex, { active: checked === true })
                }
              />
              {labels.acceptableRowActive}
            </label>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
          aria-label={labels.acceptableRemoveRow}
          onClick={() => onRemoveAcceptable(rowIndex)}
        >
          <X className="size-4" />
        </Button>
      </div>
      {betweenInvalid && labels.betweenInvalid && (
        <p className="text-xs text-destructive">{labels.betweenInvalid}</p>
      )}
    </div>
  )
}

const AcceptableValueRowCard = ({
  rowIndex,
  row,
  fieldTypeCode,
  validationTypeOptions,
  fieldValidationOptions,
  labels,
  idPrefix,
  showAcceptableRowActive,
  showValidationErrors = false,
  isContradictory = false,
  onPatchAcceptable,
  onRemoveAcceptable,
}: {
  rowIndex: number
  row: DataFieldValidationEditorProps['acceptableValues'][number]
  fieldTypeCode: string | null
  validationTypeOptions: DataFieldValidationEditorProps['validationTypeOptions']
  fieldValidationOptions: DataFieldValidationEditorProps['validationTypeOptions']
  labels: DataFieldValidationEditorProps['labels']
  idPrefix: string
  showAcceptableRowActive: boolean

  showValidationErrors?: boolean
  isContradictory?: boolean
  onPatchAcceptable: DataFieldValidationEditorProps['onPatchAcceptable']
  onRemoveAcceptable: DataFieldValidationEditorProps['onRemoveAcceptable']
}) => {
  const betweenBounds = parseBetweenBounds(row.value)
  const isBetween = isBetweenValidationType(
    row.instrumentTaskDataFieldValidationTypeId,
    validationTypeOptions,
  )
  const betweenInvalid = isBetween && !isValidBetweenBounds(betweenBounds.min, betweenBounds.max)
  const markInvalid = isContradictory && showValidationErrors

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          {labels.acceptableValue && <Label>{labels.acceptableValue}</Label>}
          {isBetween ? (
            <div className="space-y-1">
              <div className="flex gap-2">
                <Input
                  value={betweenBounds.min}
                  onChange={(event) =>
                    onPatchAcceptable(rowIndex, {
                      value: formatBetweenBounds(event.target.value, betweenBounds.max),
                    })
                  }
                  placeholder={labels.acceptableBetweenMin}
                  inputMode="decimal"
                  aria-invalid={betweenInvalid || markInvalid || undefined}
                />
                <Input
                  value={betweenBounds.max}
                  onChange={(event) =>
                    onPatchAcceptable(rowIndex, {
                      value: formatBetweenBounds(betweenBounds.min, event.target.value),
                    })
                  }
                  placeholder={labels.acceptableBetweenMax}
                  inputMode="decimal"
                  aria-invalid={betweenInvalid || markInvalid || undefined}
                />
              </div>
              {betweenInvalid && labels.betweenInvalid && (
                <p className="text-xs text-destructive">{labels.betweenInvalid}</p>
              )}
            </div>
          ) : (
            <Input
              value={row.value}
              onChange={(event) => {
                const newValue = event.target.value
                if (
                  (fieldTypeCode === 'number' || fieldTypeCode === 'linear') &&
                  newValue !== '' &&
                  !/^-?\d*\.?\d*$/.test(newValue)
                ) {
                  return
                }
                onPatchAcceptable(rowIndex, { value: newValue })
              }}
              placeholder={labels.acceptableValuePlaceholder}
              inputMode={
                fieldTypeCode === 'number' || fieldTypeCode === 'linear' ? 'decimal' : undefined
              }
              aria-invalid={markInvalid || undefined}
            />
          )}
        </div>
        <div className="space-y-1">
          <Label>{labels.acceptableValidationType}</Label>
          <Select
            value={row.instrumentTaskDataFieldValidationTypeId}
            onValueChange={(newValidationTypeId) =>
              onPatchAcceptable(rowIndex, {
                instrumentTaskDataFieldValidationTypeId: newValidationTypeId,
                ...patchForValidationTypeChange(
                  row.value,
                  row.instrumentTaskDataFieldValidationTypeId,
                  newValidationTypeId,
                  validationTypeOptions,
                ),
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder={labels.acceptableValidationTypePlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {fieldValidationOptions.map((validationOption) => (
                <SelectItem key={validationOption.id} value={String(validationOption.id)}>
                  {validationOption.validationTypeDesc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${idPrefix}-acc-ok-${rowIndex}`}
            checked={row.isAcceptable}
            onCheckedChange={(checked) =>
              onPatchAcceptable(rowIndex, { isAcceptable: checked === true })
            }
          />
          <Label htmlFor={`${idPrefix}-acc-ok-${rowIndex}`} className="font-normal">
            {labels.acceptableIsAcceptable}
          </Label>
        </div>
        {showAcceptableRowActive && labels.acceptableRowActive && (
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${idPrefix}-acc-act-${rowIndex}`}
              checked={row.active}
              onCheckedChange={(checked) =>
                onPatchAcceptable(rowIndex, { active: checked === true })
              }
            />
            <Label htmlFor={`${idPrefix}-acc-act-${rowIndex}`} className="font-normal">
              {labels.acceptableRowActive}
            </Label>
          </div>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemoveAcceptable(rowIndex)}
        >
          {labels.acceptableRemoveRow}
        </Button>
      </div>
    </div>
  )
}

export const DataFieldValidationEditor = ({
  fieldTypeCode,
  validationTypeOptions,
  acceptableValues,
  definedValues,
  onPatchAcceptable,
  onAddAcceptable,
  onRemoveAcceptable,
  onPatchDefined,
  onToggleDefinedAcceptable,
  onAddDefined,
  onRemoveDefined,
  labels,
  variant,
  showAcceptableRowActive = false,
  showAcceptableSection = true,
  showAcceptableAddRow = true,
  showValidationHeader = false,
  showDefinedCharCount = false,
  showDefinedAcceptable = true,
  showValidationErrors = false,
  idPrefix = 'data-field',
}: DataFieldValidationEditorProps) => {
  const showDefinedValues = shouldShowDefinedValues(fieldTypeCode)
  const AcceptableRow = variant === 'inline' ? AcceptableValueRowInline : AcceptableValueRowCard
  const activeDefinedCount = definedValues.filter(
    (r) => !(r as { _destroy?: boolean })._destroy,
  ).length
  const liveAcceptableValues = acceptableValues.filter((row) => !row._destroy)
  // Legacy parity (hasBetweenValidation): Between is exclusive — hide Add More while any live
  // acceptable row is Between. Switching to a non-Between type restores the button (REBUILD-515).
  const hasBetweenRuleSelected = liveAcceptableValues.some((row) =>
    isBetweenValidationType(row.instrumentTaskDataFieldValidationTypeId, validationTypeOptions),
  )

  // Contradictory acceptable conditions (REBUILD-515): rows whose numeric rules can never all
  // be satisfied together (e.g. "> 16" AND "between 5 and 10"). Flagged inline and summarised.
  const contradictoryRowIndexes = new Set(
    findContradictoryAcceptableConditions(acceptableValues, validationTypeOptions),
  )
  const showContradiction =
    showValidationErrors && contradictoryRowIndexes.size > 0 && !!labels.contradictoryConditions

  return (
    <div className={variant === 'inline' ? 'space-y-3 rounded-md border p-3' : 'space-y-3'}>
      {showAcceptableSection && (
        <div className={variant === 'inline' ? 'space-y-2' : 'space-y-3'}>
          {variant === 'card' && labels.sectionAcceptable && (
            <h2 className="text-lg font-semibold">{labels.sectionAcceptable}</h2>
          )}
          {showValidationHeader && labels.acceptableValidationHeader && (
            <p className="text-xs font-medium text-muted-foreground">
              {labels.acceptableValidationHeader}
            </p>
          )}
          {variant === 'card' && acceptableValues.length === 0 && labels.acceptableEmptyHint && (
            <p className="max-w-2xl text-sm text-muted-foreground">{labels.acceptableEmptyHint}</p>
          )}
          <div className={variant === 'card' ? 'space-y-4' : 'space-y-2'}>
            {acceptableValues.map((row, rowIndex) => (
              <React.Fragment key={row.id ?? `acceptable-${rowIndex}`}>
                {rowIndex === 1 &&
                  variant === 'inline' &&
                  (fieldTypeCode === 'number' || fieldTypeCode === 'linear') &&
                  labels.operatorTypeOr &&
                  labels.operatorTypeAnd && (
                    <div className="flex items-center gap-2 py-0.5">
                      <Select
                        value={acceptableValues[0].operatorType || 'OR'}
                        onValueChange={(value) =>
                          onPatchAcceptable(0, { operatorType: value as 'OR' | 'AND' })
                        }
                      >
                        <SelectTrigger className="w-20 h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OR">{labels.operatorTypeOr}</SelectItem>
                          <SelectItem value="AND">{labels.operatorTypeAnd}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                <AcceptableRow
                  rowIndex={rowIndex}
                  row={row}
                  fieldTypeCode={fieldTypeCode}
                  validationTypeOptions={validationTypeOptions}
                  fieldValidationOptions={validationTypeOptions}
                  labels={labels}
                  idPrefix={idPrefix}
                  showAcceptableRowActive={showAcceptableRowActive}
                  showValidationErrors={showValidationErrors}
                  isContradictory={contradictoryRowIndexes.has(rowIndex)}
                  onPatchAcceptable={onPatchAcceptable}
                  onRemoveAcceptable={onRemoveAcceptable}
                />
              </React.Fragment>
            ))}
          </div>
          {showContradiction && (
            <p className="text-sm text-destructive">{labels.contradictoryConditions}</p>
          )}
          {/* REBUILD-431 (JW 417): single-rule surfaces (e.g. linked-supply Linear) pass
              showAcceptableAddRow=false to hide "Add More Validation" entirely. REBUILD-614:
              Number accepts one live rule. REBUILD-515 also hides the control while Between is
              selected on any live row (legacy hasBetweenValidation). */}
          {showAcceptableAddRow &&
            !hasBetweenRuleSelected &&
            (fieldTypeCode !== 'number' ||
              canAddNumberAcceptableRule(liveAcceptableValues.length)) && (
              <Button type="button" variant="outline" size="sm" onClick={onAddAcceptable}>
                {labels.acceptableAddRow}
              </Button>
            )}
        </div>
      )}

      {showDefinedValues && (
        <div className={variant === 'card' ? 'space-y-3' : 'space-y-2'}>
          {variant === 'card' && labels.sectionDefined && (
            <h2 className="text-lg font-semibold">{labels.sectionDefined}</h2>
          )}
          {variant === 'inline' && labels.definedValuesTitle && (
            <p className="text-xs font-medium text-muted-foreground">{labels.definedValuesTitle}</p>
          )}
          <div className="space-y-2">
            {definedValues.map((row, rowIndex) =>
              variant === 'inline' ? (
                <div key={row.id ?? `defined-${rowIndex}`} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Input
                      value={row.value}
                      maxLength={DATA_FIELD_TEXT_MAX_LENGTH}
                      onChange={(event) => onPatchDefined(rowIndex, event.target.value)}
                      placeholder={labels.definedValuePlaceholder}
                      aria-invalid={(showValidationErrors && row.value.trim() === '') || undefined}
                    />
                    {showDefinedAcceptable && (
                      <label className="flex shrink-0 items-center gap-1.5 text-xs">
                        <Checkbox
                          id={`${idPrefix}-def-ok-${rowIndex}`}
                          checked={row.isAcceptable}
                          onCheckedChange={(checked) =>
                            onToggleDefinedAcceptable(rowIndex, checked === true)
                          }
                        />
                        {labels.definedIsAcceptable}
                      </label>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={activeDefinedCount <= 1}
                      onClick={() => onRemoveDefined(rowIndex)}
                    >
                      {labels.definedRemove}
                    </Button>
                  </div>
                  {showDefinedCharCount && (
                    <p className="text-right text-xs text-muted-foreground">
                      {row.value.length}/{DATA_FIELD_TEXT_MAX_LENGTH}
                    </p>
                  )}
                </div>
              ) : (
                <div key={row.id ?? `defined-${rowIndex}`} className="flex items-end gap-2">
                  <div className="flex-1 space-y-1">
                    {labels.definedValue && <Label>{labels.definedValue}</Label>}
                    <Input
                      value={row.value}
                      onChange={(event) => onPatchDefined(rowIndex, event.target.value)}
                      placeholder={labels.definedValuePlaceholder}
                      maxLength={DATA_FIELD_TEXT_MAX_LENGTH}
                    />
                    {showDefinedCharCount && (
                      <p className="text-right text-xs text-muted-foreground">
                        {row.value.length}/{DATA_FIELD_TEXT_MAX_LENGTH}
                      </p>
                    )}
                  </div>
                  {showDefinedAcceptable && (
                    <label className="flex shrink-0 items-center gap-1.5 pb-0.5 text-sm">
                      <Checkbox
                        id={`${idPrefix}-def-ok-${rowIndex}`}
                        checked={row.isAcceptable}
                        onCheckedChange={(checked) =>
                          onToggleDefinedAcceptable(rowIndex, checked === true)
                        }
                      />
                      {labels.definedIsAcceptable}
                    </label>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={activeDefinedCount <= 1}
                    onClick={() => onRemoveDefined(rowIndex)}
                  >
                    {labels.definedRemove}
                  </Button>
                </div>
              ),
            )}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={onAddDefined}>
            {labels.definedAddRow}
          </Button>
          {showValidationErrors &&
            showDefinedAcceptable &&
            labels.definedAcceptableRequired &&
            activeDefinedCount > 0 &&
            !definedValues
              .filter((r) => !(r as { _destroy?: boolean })._destroy)
              .some((r) => r.isAcceptable) && (
              <p className="text-sm text-destructive">{labels.definedAcceptableRequired}</p>
            )}
        </div>
      )}
    </div>
  )
}
